import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Package, Truck, CheckCircle as CheckCircle2, Clock, MapPin, MagnifyingGlass as Search, ArrowLeft } from "@phosphor-icons/react";
import { formatApiErrorDetail } from "@/lib/AdminAuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_META = {
  pending_payment: { label: "Awaiting payment", color: "bg-gray-200 text-gray-800", icon: Clock, step: 0 },
  processing: { label: "Processing", color: "bg-yellow-200 text-yellow-900", icon: Package, step: 1 },
  shipped: { label: "Shipped", color: "bg-blue-200 text-blue-900", icon: Truck, step: 2 },
  delivered: { label: "Delivered", color: "bg-green-200 text-green-900", icon: CheckCircle2, step: 3 },
  canceled: { label: "Canceled", color: "bg-red-200 text-red-900", icon: Clock, step: -1 },
};

const CARRIER_URLS = {
  USPS: (n) => `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(n)}`,
  UPS: (n) => `https://www.ups.com/track?tracknum=${encodeURIComponent(n)}`,
  FedEx: (n) => `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(n)}`,
  DHL: (n) => `https://www.dhl.com/en/express/tracking.html?AWB=${encodeURIComponent(n)}`,
};

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function OrderTrack() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [orderId, setOrderId] = useState(searchParams.get("order_id") || "");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    if (e) e.preventDefault();
    if (!email.trim() || !orderId.trim()) {
      toast.error("Enter both email and order ID");
      return;
    }
    setLoading(true);
    setOrder(null);
    try {
      const res = await axios.post(`${API}/orders/lookup`, {
        email: email.trim().toLowerCase(),
        order_id: orderId.trim(),
      });
      setOrder(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Order not found");
    } finally {
      setLoading(false);
    }
  };

  // Auto-lookup if both query params are present
  useEffect(() => {
    if (searchParams.get("email") && searchParams.get("order_id")) {
      lookup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#080412] text-white font-sans" data-testid="track-page">
      <header className="border-b border-white/8 bg-[#080412]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="track-brand-logo">
            <img src="/assets/hero-logo.png" alt="House Rules" className="h-12 md:h-14 w-auto rounded-lg drop-shadow-[0_0_20px_rgba(167,66,255,0.5)]" />
          </Link>
          <Link to="/" className="font-sora text-[10.5px] font-semibold uppercase tracking-[0.24em] flex items-center gap-2 text-white/80 hover:text-brandpink transition-colors" data-testid="track-back-link">
            <ArrowLeft size={14} weight="bold" /> Back to Site
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-10 md:py-14">
        <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.32em] text-brandpink mb-2">Order tracking</div>
        <h1 className="font-display uppercase text-5xl sm:text-6xl leading-[0.9] tracking-[-0.01em] mb-3" data-testid="track-headline">
          Where&apos;s my <span className="italic font-editorial text-brandpink hr-text-glow-pink">box?</span>
        </h1>
        <p className="font-sans text-base text-white/60 mb-8 max-w-xl">
          Enter the email you used at checkout and your order ID (we sent it in the confirmation email) to see your shipment status.
        </p>

        <form onSubmit={lookup} className="rounded-2xl hr-glass p-6 grid sm:grid-cols-5 gap-3 items-end mb-8">
          <div className="sm:col-span-2">
            <label className="font-sora text-[10px] font-semibold uppercase tracking-[0.24em] text-white/60 mb-1.5 block">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="w-full px-3 py-2.5 font-sans text-sm bg-white/[0.04] border border-white/15 rounded-lg focus:outline-none focus:border-brandpink text-white placeholder-white/40" data-testid="track-input-email" required />
          </div>
          <div className="sm:col-span-2">
            <label className="font-sora text-[10px] font-semibold uppercase tracking-[0.24em] text-white/60 mb-1.5 block">Order ID</label>
            <input value={orderId} onChange={(e) => setOrderId(e.target.value)} placeholder="e.g. 59804180-3326-…" className="w-full px-3 py-2.5 font-mono text-xs bg-white/[0.04] border border-white/15 rounded-lg focus:outline-none focus:border-brandpink text-white placeholder-white/40" data-testid="track-input-order-id" required />
          </div>
          <button type="submit" disabled={loading} className="hr-cta h-11 rounded-full text-white font-sora font-semibold text-[10.5px] uppercase tracking-[0.22em] flex items-center justify-center gap-2 disabled:opacity-50" data-testid="track-submit">
            <Search size={14} weight="bold" /> {loading ? "…" : "Track"}
          </button>
        </form>

        {order && <OrderDetails order={order} />}
      </main>
    </div>
  );
}

function OrderDetails({ order }) {
  const meta = STATUS_META[order.status] || STATUS_META.processing;
  const Icon = meta.icon;
  const trackingUrl = order.tracking_number && order.tracking_carrier && CARRIER_URLS[order.tracking_carrier]
    ? CARRIER_URLS[order.tracking_carrier](order.tracking_number)
    : null;

  const steps = [
    { key: "processing", label: "Processing", icon: Package },
    { key: "shipped", label: "Shipped", icon: Truck },
    { key: "delivered", label: "Delivered", icon: CheckCircle2 },
  ];
  const currentStep = meta.step;

  return (
    <div className="space-y-6" data-testid="track-order-details">
      <div className="rounded-2xl hr-glass hr-neon-pink p-6 sm:p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
          <div>
            <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.28em] text-white/50">Order</div>
            <div className="font-mono text-sm mt-1 text-white" data-testid="track-order-id">{order.id}</div>
          </div>
          <div
            className="inline-flex items-center gap-2 font-sora text-[10px] font-semibold uppercase tracking-[0.24em] px-3 py-1.5 rounded-full bg-white/8 border border-white/15 text-white"
            data-testid="track-status-badge"
          >
            <Icon size={12} weight="duotone" className="text-brandpink" /> {meta.label}
          </div>
        </div>

        {order.status !== "canceled" && (
          <div className="grid grid-cols-3 gap-3 mb-2" data-testid="track-steps">
            {steps.map((s, i) => {
              const done = currentStep >= i + 1;
              const StepIcon = s.icon;
              return (
                <div key={s.key} className="flex flex-col items-center text-center">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 ${done ? "hr-cta" : "bg-white/[0.06] border border-white/15 text-white/50"}`}>
                    <StepIcon size={18} weight="duotone" className={done ? "text-white" : ""} />
                  </div>
                  <div className={`font-sora text-[10px] font-semibold uppercase tracking-[0.22em] ${done ? "text-brandpink" : "text-white/40"}`}>
                    {s.label}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {order.tracking_number && (
          <div className="mt-6 rounded-xl bg-white/[0.04] border border-white/10 p-4">
            <div className="font-sora text-[10px] font-semibold uppercase tracking-[0.24em] text-white/50 mb-1">
              {order.tracking_carrier || "Tracking"} number
            </div>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="font-mono text-base text-white" data-testid="track-tracking-number">{order.tracking_number}</div>
              {trackingUrl && (
                <a href={trackingUrl} target="_blank" rel="noopener noreferrer"
                  className="hr-cta font-sora font-semibold text-[10px] uppercase tracking-[0.22em] px-4 h-9 rounded-full flex items-center gap-2 text-white"
                  data-testid="track-carrier-link">
                  Track with {order.tracking_carrier?.toUpperCase()}
                </a>
              )}
            </div>
          </div>
        )}

        {!order.tracking_number && order.status === "processing" && (
          <p className="mt-6 font-sans text-sm text-white/60">
            We&apos;re packing your order. You&apos;ll receive a tracking number by email as soon as it ships (usually within 3–5 business days).
          </p>
        )}

        {order.status === "pending_payment" && (
          <p className="mt-6 font-sans text-sm text-white/60">
            Your payment hasn&apos;t cleared yet. If you completed checkout, this can take a few minutes - please refresh shortly.
          </p>
        )}
      </div>

      <div className="rounded-2xl hr-glass p-6 sm:p-8">
        <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.32em] text-brandpink mb-4">Items</div>
        <div className="space-y-3">
          {order.items?.map((it, i) => (
            <div key={i} className="flex items-center gap-3" data-testid="track-item">
              {it.image && (
                <img src={it.image} alt="" className="w-12 h-12 rounded-lg border border-white/12 object-cover" />
              )}
              <div className="flex-1">
                <div className="font-sans text-sm text-white">{it.name}</div>
                <div className="font-sans text-xs text-white/50">Qty {it.qty}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="border-t border-white/10 mt-4 pt-3 flex justify-between font-anton text-2xl text-white">
          <span>Total</span>
          <span data-testid="track-total">{fmt(order.total)}</span>
        </div>
      </div>

      <div className="rounded-2xl hr-glass p-6 sm:p-8">
        <div className="flex items-center gap-2 mb-3">
          <MapPin size={14} weight="duotone" className="text-brandpink" />
          <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.32em] text-brandpink">Shipping to</div>
        </div>
        <div className="font-sans text-sm text-white/90">
          <div>{order.customer_name}</div>
          <div className="text-white/60">
            {order.ship_to?.city}, {order.ship_to?.state}, {order.ship_to?.country}
          </div>
        </div>
      </div>
    </div>
  );
}
